import React, { useReducer, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Popover,
  Typography,
  CircularProgress,
  Divider,
  Badge,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as ApproveIcon,
} from '@mui/icons-material';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import { useViewport } from '../../../shared/hooks/useViewport';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import { extractErrorMessage } from '../../../shared/utils/errorUtils';
import { useEscalationControl } from '../hooks/useEscalationControl';
import type { EscalationResponse } from '../../../types';
import { viewportGutterMaxWidth } from '../../../shared/constants/layout';
import EscalationRequestForm, { type EscalationDuration, type EscalationLevel } from './EscalationRequestForm';

interface EscalationControlProps {
  customerId: string;
  onEffectiveLevelChange?: (effectiveLevel: string, ceiling: string) => void;
}

function formatTimeRemaining(expiresAt: string): string {
  const remaining = new Date(expiresAt).getTime() - Date.now();
  if (remaining <= 0) return 'expired';
  const minutes = Math.ceil(remaining / 60000);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  }
  return `${minutes}m`;
}

// Local, non-server UI state: the escalation-request form and its popover.
type FormState = {
  selectedLevel: EscalationLevel;
  selectedDuration: EscalationDuration;
  anchorEl: HTMLElement | null;
};

type FormAction =
  | { type: 'SET_SELECTED_LEVEL'; payload: EscalationLevel }
  | { type: 'SET_SELECTED_DURATION'; payload: EscalationDuration }
  | { type: 'SET_ANCHOR_EL'; payload: HTMLElement | null };

// Escalations are keyed on the Cognito subject; the email is display-only and is
// absent when the requesting token could not supply one. Showing a raw subject
// UUID tells an approver nothing, so say so plainly and keep the id short enough
// to correlate with the record.
function escalationLabel(esc: EscalationResponse): string {
  const localPart = esc.user_email?.split('@')[0];
  return localPart || `unknown user (${esc.user_id.slice(0, 8)})`;
}

const initialFormState: FormState = {
  selectedLevel: 'l2',
  selectedDuration: 120,
  anchorEl: null,
};

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SET_SELECTED_LEVEL':
      return { ...state, selectedLevel: action.payload };
    case 'SET_SELECTED_DURATION':
      return { ...state, selectedDuration: action.payload };
    case 'SET_ANCHOR_EL':
      return { ...state, anchorEl: action.payload };
  }
}

const EscalationControl: React.FC<EscalationControlProps> = ({ customerId, onEffectiveLevelChange }) => {
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const { isPhone } = useViewport();

  const {
    loading,
    activeEscalation,
    pendingApprovals,
    effectiveLevel,
    ceiling,
    acting,
    approvingUserId,
    requestEscalation,
    revokeEscalation,
    approveEscalation,
  } = useEscalationControl(customerId);

  const [form, dispatch] = useReducer(formReducer, initialFormState);
  const { selectedLevel, selectedDuration, anchorEl } = form;

  const [timeDisplay, setTimeDisplay] = useState('');

  // Countdown timer — pure UI clock off expires_at, independent of server polling.
  const expiresAt = activeEscalation?.expires_at;
  useEffect(() => {
    if (!expiresAt) {
      setTimeDisplay('');
      return;
    }

    // Key on expiresAt, not the whole escalation object — the 60s poll re-derives
    // a fresh object each time, and only expiry changes should reset the clock.
    const update = () => setTimeDisplay(formatTimeRemaining(expiresAt));
    update();
    const interval = setInterval(update, 30000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const handleRequestEscalation = useCallback(async () => {
    try {
      await requestEscalation({ requested_level: selectedLevel, duration_minutes: selectedDuration });
      dispatch({ type: 'SET_ANCHOR_EL', payload: null });
    } catch (err) {
      showSnackbar(extractErrorMessage(err, 'Failed to request escalation'), 'error');
    }
  }, [selectedLevel, selectedDuration, requestEscalation, showSnackbar]);

  const handleRevoke = useCallback(async (userId: string) => {
    try {
      await revokeEscalation(userId);
    } catch (err) {
      showSnackbar(extractErrorMessage(err, 'Failed to revoke escalation'), 'error');
    }
  }, [revokeEscalation, showSnackbar]);

  const handleApprove = useCallback(async (userId: string) => {
    try {
      await approveEscalation(userId);
    } catch (err) {
      showSnackbar(extractErrorMessage(err, 'Failed to approve escalation'), 'error');
    }
  }, [approveEscalation, showSnackbar]);

  useEffect(() => {
    if (!loading) onEffectiveLevelChange?.(effectiveLevel, ceiling);
  }, [effectiveLevel, ceiling, loading, onEffectiveLevelChange]);

  if (loading) return null;
  if (ceiling === 'none' && pendingApprovals.length === 0) return null;

  const canEscalate = !activeEscalation && ceiling !== 'none' && ceiling !== 'l1';

  const closeForm = () => dispatch({ type: 'SET_ANCHOR_EL', payload: null });

  // One form, two containers: a full-width dialog on a phone, where an anchored
  // popover under a header chip can land off screen, and the popover elsewhere.
  const requestForm = (
    <EscalationRequestForm
      ceiling={ceiling}
      selectedLevel={selectedLevel}
      selectedDuration={selectedDuration}
      acting={acting}
      onLevelChange={(level) => dispatch({ type: 'SET_SELECTED_LEVEL', payload: level })}
      onDurationChange={(duration) => dispatch({ type: 'SET_SELECTED_DURATION', payload: duration })}
      onSubmit={handleRequestEscalation}
    />
  );

  const levelColor = effectiveLevel === 'admin' ? 'error' : effectiveLevel === 'l2' ? 'warning' : 'default';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
      {/* Active escalation badge */}
      {activeEscalation?.status === 'active' && activeEscalation.expires_at && (
        <Chip
          label={`${activeEscalation.escalation_level.toUpperCase()} (${timeDisplay})`}
          color="warning"
          size="small"
          variant="outlined"
          onDelete={() => handleRevoke(activeEscalation.user_id)}
          deleteIcon={acting ? <CircularProgress size={14} /> : <CloseIcon fontSize="small" />}
          sx={{ mr: 1 }}
        />
      )}

      {/* Pending escalation badge */}
      {activeEscalation?.status === 'pending' && (
        <Chip
          label={`${activeEscalation.escalation_level.toUpperCase()} pending`}
          color="info"
          size="small"
          variant="outlined"
          onDelete={() => handleRevoke(activeEscalation.user_id)}
          deleteIcon={acting ? <CircularProgress size={14} /> : <CloseIcon fontSize="small" />}
          sx={{ mr: 1 }}
        />
      )}

      {/* Current level chip — clickable to escalate when ceiling > L1 */}
      {!activeEscalation && ceiling !== 'none' && (
        <>
          <Chip
            label={effectiveLevel.toUpperCase()}
            color={levelColor as 'default' | 'warning' | 'error'}
            size="small"
            variant="outlined"
            onClick={canEscalate ? (e) => dispatch({ type: 'SET_ANCHOR_EL', payload: e.currentTarget as HTMLElement }) : undefined}
            sx={{ mr: 1, cursor: canEscalate ? 'pointer' : 'default' }}
          />

          {canEscalate && isPhone && (
            <Dialog open={Boolean(anchorEl)} onClose={closeForm} fullWidth maxWidth="xs">
              <DialogTitle>Request Escalation</DialogTitle>
              <DialogContent>
                <Box sx={{ pt: 1 }}>{requestForm}</Box>
              </DialogContent>
            </Dialog>
          )}

          {canEscalate && !isPhone && (
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={closeForm}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Box sx={{ p: 2, minWidth: 240, maxWidth: viewportGutterMaxWidth }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>Request Escalation</Typography>
                {requestForm}
              </Box>
            </Popover>
          )}
        </>
      )}
      {/* Pending approvals for admins */}
      {pendingApprovals.length > 0 && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          {pendingApprovals.map(esc => (
            <Chip
              key={esc.user_id}
              label={`${escalationLabel(esc)} → ${esc.escalation_level.toUpperCase()}`}
              color="info"
              size="small"
              variant="outlined"
              icon={
                approvingUserId === esc.user_id
                  ? <CircularProgress size={14} />
                  : <Badge color="warning" variant="dot"><ApproveIcon fontSize="small" /></Badge>
              }
              onClick={() => handleApprove(esc.user_id)}
              sx={{ mr: 0.5, cursor: 'pointer' }}
            />
          ))}
        </>
      )}
      <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} />
    </Box>
  );
};

export default EscalationControl;
